/* eslint-disable curly */
import { DefaultSidebar, Sidebar } from "@excalidraw/excalidraw";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { useCallback, useEffect, useState } from "react";

import * as libraryStore from "../data/libraryStore";

import { ConfirmDialog, NewCanvasDialog, PromptDialog } from "./LibraryDialogs";

import "./CanvasLibrarySidebar.scss";

import type { CanvasMeta, Folder } from "../data/libraryApi";

export const CANVAS_LIBRARY_TAB = "canvas-library";

const DRAG_MIME = "application/x-excalidraw-canvas-id";

interface Props {
  activeCanvasId: string | null;
  onSelectCanvas: (id: string) => void;
  onCanvasDeleted: (id: string) => void;
  onLibraryChanged: () => void;
  onOpenVersionHistory: (id: string) => void;
  reloadKey: number;
}

type DialogState =
  | { type: "rename-canvas"; canvas: CanvasMeta }
  | { type: "rename-folder"; folder: Folder }
  | { type: "new-canvas"; folderId: string | null }
  | { type: "new-folder" }
  | { type: "duplicate-canvas"; canvas: CanvasMeta }
  | { type: "delete-canvas"; canvas: CanvasMeta }
  | { type: "delete-folder"; folder: Folder };

type DragHover =
  | { kind: "folder"; folderId: string | null }
  | { kind: "canvas"; canvasId: string; position: "before" | "after" }
  | null;

// History (clock with a counter-clockwise arrow) — SVG, design-system rule.
const historyIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l3 3" />
  </svg>
);

// Two overlapping rects — duplicate/copy.
const duplicateIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const folderIcon = (
  <svg
    aria-hidden="true"
    focusable="false"
    role="img"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

export const CanvasLibrarySidebar = ({
  activeCanvasId,
  onSelectCanvas,
  onCanvasDeleted,
  onLibraryChanged,
  onOpenVersionHistory,
  reloadKey,
}: Props) => {
  const { t } = useI18n();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [canvases, setCanvases] = useState<CanvasMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dragHover, setDragHover] = useState<DragHover>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const { folders: f, canvases: c } = await libraryStore.getLibrary();
      setFolders(f);
      setCanvases(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, reloadKey]);

  const notifyChange = useCallback(async () => {
    await refresh();
    onLibraryChanged();
  }, [refresh, onLibraryChanged]);

  const closeDialog = () => setDialog(null);

  const handleNewCanvas = async (input: {
    name: string;
    canvas_type: "canvas" | "notebook";
    page_style: "blank" | "ruled" | "grid" | "dotted";
  }) => {
    if (dialog?.type !== "new-canvas") return;
    try {
      const c = await libraryStore.createCanvas({
        name: input.name,
        folder_id: dialog.folderId,
        canvas_type: input.canvas_type,
        page_style:
          input.canvas_type === "notebook" ? input.page_style : undefined,
      });
      await notifyChange();
      onSelectCanvas(c.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      closeDialog();
    }
  };

  const handlePromptSubmit = async (value: string) => {
    if (!dialog) return;
    try {
      switch (dialog.type) {
        case "new-folder": {
          await libraryStore.createFolder({ name: value });
          await notifyChange();
          break;
        }
        case "rename-canvas": {
          await libraryStore.updateCanvas(dialog.canvas.id, { name: value });
          await notifyChange();
          break;
        }
        case "duplicate-canvas": {
          const copy = await libraryStore.duplicateCanvas(dialog.canvas, value);
          await notifyChange();
          onSelectCanvas(copy.id);
          break;
        }
        case "rename-folder": {
          await libraryStore.updateFolder(dialog.folder.id, { name: value });
          await notifyChange();
          break;
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      closeDialog();
    }
  };

  const handleConfirm = async () => {
    if (!dialog) return;
    try {
      switch (dialog.type) {
        case "delete-canvas": {
          await libraryStore.deleteCanvas(dialog.canvas.id);
          if (dialog.canvas.id === activeCanvasId) {
            onCanvasDeleted(dialog.canvas.id);
          }
          await notifyChange();
          break;
        }
        case "delete-folder": {
          await libraryStore.deleteFolder(dialog.folder.id);
          await notifyChange();
          break;
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleFolder = (id: string) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onCanvasDragStart = (
    c: CanvasMeta,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.dataTransfer.setData(DRAG_MIME, c.id);
    e.dataTransfer.setData("text/plain", c.id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(c.id);
  };

  const onCanvasDragEnd = () => {
    setDraggingId(null);
    setDragHover(null);
  };

  const readDragId = (e: React.DragEvent): string | null => {
    return (
      e.dataTransfer.getData(DRAG_MIME) ||
      e.dataTransfer.getData("text/plain") ||
      null
    );
  };

  const onCanvasDragOver = (
    target: CanvasMeta,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (draggingId === target.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isBefore = (e.clientY - rect.top) / rect.height < 0.5;
    setDragHover({
      kind: "canvas",
      canvasId: target.id,
      position: isBefore ? "before" : "after",
    });
  };

  const onCanvasDrop = async (
    target: CanvasMeta,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const id = readDragId(e);
    setDragHover(null);
    setDraggingId(null);
    if (!id || id === target.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isBefore = (e.clientY - rect.top) / rect.height < 0.5;
    const newPosition = isBefore
      ? target.position - 0.5
      : target.position + 0.5;
    try {
      await libraryStore.updateCanvas(id, {
        folder_id: target.folder_id,
        position: newPosition,
      });
      await notifyChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onFolderDragOver = (
    folderId: string | null,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragHover({ kind: "folder", folderId });
  };

  const onFolderDrop = async (
    folderId: string | null,
    e: React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const id = readDragId(e);
    setDragHover(null);
    setDraggingId(null);
    if (!id) return;
    const target = canvases.find((c) => c.id === id);
    if (!target) return;
    if (target.folder_id === folderId) return;
    const siblings = canvases.filter((c) => c.folder_id === folderId);
    const maxPos = siblings.reduce((m, c) => Math.max(m, c.position), -1);
    try {
      await libraryStore.updateCanvas(id, {
        folder_id: folderId,
        position: maxPos + 1,
      });
      await notifyChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  // Canvases shared WITH me by another account are surfaced in their own
  // section (not draggable / not mine to rename/delete), so keep them out of
  // the root + folder listings.
  const rootCanvases = canvases.filter(
    (c) => c.folder_id === null && !c.shared,
  );
  const sharedCanvases = canvases.filter((c) => c.shared);
  const rootFolders = folders.filter((f) => f.parent_id === null);
  const canvasesByFolder = (fid: string) =>
    canvases.filter((c) => c.folder_id === fid && !c.shared);

  const renderCanvas = (c: CanvasMeta) => {
    const isHovering =
      dragHover?.kind === "canvas" && dragHover.canvasId === c.id;
    const indicatorBefore = isHovering && dragHover.position === "before";
    const indicatorAfter = isHovering && dragHover.position === "after";
    return (
      <div
        key={c.id}
        className={`canvas-library__item${
          c.id === activeCanvasId ? " is-active" : ""
        }${draggingId === c.id ? " is-dragging" : ""}${
          indicatorBefore ? " drop-before" : ""
        }${indicatorAfter ? " drop-after" : ""}`}
        draggable
        onDragStart={(e) => onCanvasDragStart(c, e)}
        onDragEnd={onCanvasDragEnd}
        onDragOver={(e) => onCanvasDragOver(c, e)}
        onDrop={(e) => onCanvasDrop(c, e)}
        onClick={() => onSelectCanvas(c.id)}
        title={t("canvasLibrary.updatedAt", {
          date: new Date(c.updated_at).toLocaleString(),
        })}
      >
        <span className="canvas-library__item-name">{c.name}</span>
        <span className="canvas-library__item-controls">
          <button
            type="button"
            className="canvas-library__icon-btn"
            title={t("versionHistory.open")}
            aria-label={t("versionHistory.open")}
            onClick={(e) => {
              e.stopPropagation();
              onOpenVersionHistory(c.id);
            }}
          >
            {historyIcon}
          </button>
          <button
            type="button"
            className="canvas-library__icon-btn"
            title={t("canvasLibrary.duplicateCanvas")}
            aria-label={t("canvasLibrary.duplicateCanvas")}
            onClick={(e) => {
              e.stopPropagation();
              setDialog({ type: "duplicate-canvas", canvas: c });
            }}
          >
            {duplicateIcon}
          </button>
          <button
            type="button"
            className="canvas-library__icon-btn"
            title={t("canvasLibrary.rename")}
            onClick={(e) => {
              e.stopPropagation();
              setDialog({ type: "rename-canvas", canvas: c });
            }}
          >
            ✎
          </button>
          <button
            type="button"
            className="canvas-library__icon-btn"
            title={t("canvasLibrary.delete")}
            onClick={(e) => {
              e.stopPropagation();
              setDialog({ type: "delete-canvas", canvas: c });
            }}
          >
            ✕
          </button>
        </span>
      </div>
    );
  };

  // Read-only row for a board shared with me: open it, but no drag/rename/delete
  // (the owner controls those). Shows who shared it.
  const renderSharedCanvas = (c: CanvasMeta) => (
    <div
      key={c.id}
      className={`canvas-library__item canvas-library__item--shared${
        c.id === activeCanvasId ? " is-active" : ""
      }`}
      onClick={() => onSelectCanvas(c.id)}
      title={
        c.owner_username
          ? t("collab.sharedBy", { name: c.owner_username })
          : c.name
      }
    >
      <span className="canvas-library__item-name">{c.name}</span>
      {c.owner_username && (
        <span className="canvas-library__item-owner">@{c.owner_username}</span>
      )}
    </div>
  );

  const isFolderDropTarget = (fid: string | null) =>
    dragHover?.kind === "folder" && dragHover.folderId === fid;

  return (
    <DefaultSidebar>
      <DefaultSidebar.TabTriggers>
        <Sidebar.TabTrigger tab={CANVAS_LIBRARY_TAB}>
          {folderIcon}
        </Sidebar.TabTrigger>
      </DefaultSidebar.TabTriggers>
      <Sidebar.Tab tab={CANVAS_LIBRARY_TAB}>
        <div className="canvas-library">
          <div className="canvas-library__actions">
            <button
              type="button"
              className="canvas-library__btn"
              onClick={() => setDialog({ type: "new-canvas", folderId: null })}
            >
              {t("canvasLibrary.addCanvas")}
            </button>
            <button
              type="button"
              className="canvas-library__btn"
              onClick={() => setDialog({ type: "new-folder" })}
            >
              {t("canvasLibrary.addFolder")}
            </button>
            <button
              type="button"
              className="canvas-library__btn"
              onClick={refresh}
              title={t("canvasLibrary.refresh")}
            >
              ↻
            </button>
          </div>

          {error && (
            <div
              className="canvas-library__error"
              onClick={() => setError(null)}
            >
              {error}
            </div>
          )}

          {loading && (
            <div className="canvas-library__empty">
              {t("canvasLibrary.loading")}
            </div>
          )}

          {!loading && canvases.length === 0 && folders.length === 0 && (
            <div className="canvas-library__empty">
              {t("canvasLibrary.empty")}
              <br />
              {t("canvasLibrary.emptyAction", {
                action: t("canvasLibrary.addCanvas"),
              })}
            </div>
          )}

          <div
            className={`canvas-library__root-drop${
              isFolderDropTarget(null) ? " is-drop-target" : ""
            }`}
            onDragOver={(e) => onFolderDragOver(null, e)}
            onDrop={(e) => onFolderDrop(null, e)}
          >
            {rootCanvases.length > 0 ? (
              rootCanvases.map(renderCanvas)
            ) : !loading && rootFolders.length > 0 ? (
              <div className="canvas-library__hint">
                {t("canvasLibrary.dropToRoot")}
              </div>
            ) : null}
          </div>

          {rootFolders.map((f) => {
            const isCollapsed = collapsedFolders.has(f.id);
            const folderCanvases = canvasesByFolder(f.id);
            const isTarget = isFolderDropTarget(f.id);
            return (
              <div
                key={f.id}
                className={`canvas-library__folder${
                  isTarget ? " is-drop-target" : ""
                }`}
              >
                <div
                  className="canvas-library__folder-header"
                  onClick={() => toggleFolder(f.id)}
                  onDragOver={(e) => onFolderDragOver(f.id, e)}
                  onDrop={(e) => onFolderDrop(f.id, e)}
                >
                  <span>
                    {isCollapsed ? "▶" : "▼"} {f.name} ({folderCanvases.length})
                  </span>
                  <span className="canvas-library__item-controls">
                    <button
                      type="button"
                      className="canvas-library__icon-btn"
                      title={t("canvasLibrary.newCanvasInFolder")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialog({ type: "new-canvas", folderId: f.id });
                      }}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="canvas-library__icon-btn"
                      title={t("canvasLibrary.rename")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialog({ type: "rename-folder", folder: f });
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="canvas-library__icon-btn"
                      title={t("canvasLibrary.delete")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDialog({ type: "delete-folder", folder: f });
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </div>
                {!isCollapsed && (
                  <div
                    className="canvas-library__folder-children"
                    onDragOver={(e) => onFolderDragOver(f.id, e)}
                    onDrop={(e) => onFolderDrop(f.id, e)}
                  >
                    {folderCanvases.length === 0 ? (
                      <div className="canvas-library__empty">
                        {t("canvasLibrary.emptyFolder")}
                      </div>
                    ) : (
                      folderCanvases.map(renderCanvas)
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {sharedCanvases.length > 0 && (
            <div className="canvas-library__shared">
              <div className="canvas-library__shared-header">
                {t("collab.sharedWithMe")}
              </div>
              {sharedCanvases.map(renderSharedCanvas)}
            </div>
          )}
        </div>

        {dialog?.type === "new-canvas" && (
          <NewCanvasDialog
            initialValue={t("canvasLibrary.untitled")}
            onSubmit={handleNewCanvas}
            onClose={closeDialog}
          />
        )}

        {(dialog?.type === "new-folder" ||
          dialog?.type === "rename-canvas" ||
          dialog?.type === "rename-folder" ||
          dialog?.type === "duplicate-canvas") && (
          <PromptDialog
            title={
              dialog.type === "new-folder"
                ? t("canvasLibrary.newFolder")
                : dialog.type === "rename-canvas"
                ? t("canvasLibrary.renameCanvas")
                : dialog.type === "duplicate-canvas"
                ? t("canvasLibrary.duplicateCanvas")
                : t("canvasLibrary.renameFolder")
            }
            label={t("canvasLibrary.name")}
            initialValue={
              dialog.type === "new-folder"
                ? t("canvasLibrary.newFolder")
                : dialog.type === "rename-canvas"
                ? dialog.canvas.name
                : dialog.type === "duplicate-canvas"
                ? t("canvasLibrary.copyName", { name: dialog.canvas.name })
                : dialog.folder.name
            }
            submitLabel={
              dialog.type === "new-folder" || dialog.type === "duplicate-canvas"
                ? t("canvasLibrary.create")
                : t("canvasLibrary.save")
            }
            onSubmit={handlePromptSubmit}
            onClose={closeDialog}
          />
        )}

        {dialog?.type === "delete-canvas" && (
          <ConfirmDialog
            title={t("canvasLibrary.deleteCanvas")}
            message={t("canvasLibrary.deleteCanvasConfirm", {
              name: dialog.canvas.name,
            })}
            confirmLabel={t("canvasLibrary.delete")}
            danger
            onConfirm={handleConfirm}
            onClose={closeDialog}
          />
        )}
        {dialog?.type === "delete-folder" && (
          <ConfirmDialog
            title={t("canvasLibrary.deleteFolder")}
            message={t("canvasLibrary.deleteFolderConfirm", {
              name: dialog.folder.name,
            })}
            confirmLabel={t("canvasLibrary.delete")}
            danger
            onConfirm={handleConfirm}
            onClose={closeDialog}
          />
        )}
      </Sidebar.Tab>
    </DefaultSidebar>
  );
};
